/**
 * Dropdown Menu
 *
 * Hover/keyboard/touch interactions for primary navigation with mega menus.
 * Desktop: hover to open with configurable close delay.
 * Mobile: not handled here (WP block nav overlay used for mobile).
 */

const HOVER_CLOSE_DELAY = 400;

function initDropdownMenu( nav ) {
	let hoverTimeout;

	/**
	 * Get all ancestor LI.menu-item-has-children inside the nav.
	 */
	function getAncestors( el ) {
		const result = [];
		let node = el.parentElement;
		while ( node && node !== nav ) {
			if ( node.classList.contains( 'menu-item-has-children' ) ||
				node.classList.contains( 'menu-item' ) && node.querySelector( ':scope > .mega-menu__wrapper' ) ) {
				result.push( node );
			}
			node = node.parentElement;
		}
		return result;
	}

	function activateItem( li ) {
		if ( ! li ) return;
		li.setAttribute( 'aria-expanded', 'true' );

		const sub = li.querySelector( ':scope > .sub-menu, :scope > .mega-menu__wrapper' );
		if ( sub ) {
			sub.setAttribute( 'aria-hidden', 'false' );
		}

		const caret = li.querySelector( ':scope > span > .menu-item__caret' );
		if ( caret ) {
			caret.setAttribute( 'aria-expanded', 'true' );
		}

		// Also activate ancestors
		for ( const ancestor of getAncestors( li ) ) {
			ancestor.setAttribute( 'aria-expanded', 'true' );
			const ancestorSub = ancestor.querySelector( ':scope > .sub-menu, :scope > .mega-menu__wrapper' );
			if ( ancestorSub ) {
				ancestorSub.setAttribute( 'aria-hidden', 'false' );
			}
		}
	}

	function deactivateItem( li ) {
		if ( ! li ) return;
		li.setAttribute( 'aria-expanded', 'false' );

		const sub = li.querySelector( ':scope > .sub-menu, :scope > .mega-menu__wrapper' );
		if ( sub ) {
			sub.setAttribute( 'aria-hidden', 'true' );
		}

		const caret = li.querySelector( ':scope > span > .menu-item__caret' );
		if ( caret ) {
			caret.setAttribute( 'aria-expanded', 'false' );
		}

		// Deactivate children
		for ( const child of li.querySelectorAll( '[aria-expanded="true"]' ) ) {
			child.setAttribute( 'aria-expanded', 'false' );
			const childSub = child.querySelector( ':scope > .sub-menu, :scope > .mega-menu__wrapper' );
			if ( childSub ) {
				childSub.setAttribute( 'aria-hidden', 'true' );
			}
		}
	}

	function deactivateAll() {
		for ( const item of nav.querySelectorAll( '[aria-expanded="true"]' ) ) {
			deactivateItem( item );
		}
	}

	// Mouse enter: activate, deactivate siblings
	function onMouseEnter( e ) {
		clearTimeout( hoverTimeout );
		const li = e.currentTarget;

		const ancestors = getAncestors( li );
		for ( const item of nav.querySelectorAll( '.menu-item-has-children, [class*="mega-menu--"]' ) ) {
			if ( item !== li && ! ancestors.includes( item ) ) {
				deactivateItem( item );
			}
		}

		activateItem( li );
	}

	// Mouse leave: deactivate after delay
	function onMouseLeave( e ) {
		const target = e.currentTarget;
		hoverTimeout = setTimeout( () => deactivateItem( target ), HOVER_CLOSE_DELAY );
	}

	// Caret click (for keyboard / touch)
	function onCaretClick( e ) {
		e.stopPropagation();
		const li = e.currentTarget.closest( '.menu-item' );
		if ( ! li ) return;

		if ( li.getAttribute( 'aria-expanded' ) === 'true' ) {
			deactivateItem( li );
		} else {
			activateItem( li );
		}
	}

	// Click on # links toggles submenu
	function onEmptyLinkClick( e ) {
		const li = e.currentTarget.closest( '.menu-item-has-children, [class*="mega-menu--"]' );
		if ( ! li ) return;
		e.preventDefault();

		if ( li.getAttribute( 'aria-expanded' ) === 'true' ) {
			deactivateItem( li );
		} else {
			activateItem( li );
		}
	}

	// Close when clicking outside
	document.addEventListener( 'click', ( e ) => {
		if ( ! nav.contains( e.target ) ) {
			deactivateAll();
		}
	} );

	// Close on Escape
	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'Escape' ) {
			deactivateAll();
		}
	} );

	// Bind events to top-level menu items
	const topItems = nav.querySelectorAll( '.menu--dropdown > .menu-item' );
	for ( const item of topItems ) {
		item.addEventListener( 'mouseenter', onMouseEnter );
		item.addEventListener( 'mouseleave', onMouseLeave );
	}

	// Bind caret clicks
	for ( const caret of nav.querySelectorAll( '.js-menu-caret' ) ) {
		caret.addEventListener( 'click', onCaretClick );
	}

	// Bind empty link clicks
	for ( const link of nav.querySelectorAll( 'a[href="#"]' ) ) {
		link.addEventListener( 'click', onEmptyLinkClick );
	}

	// Current page highlight inside mega menu blocks
	const currentUrl = window.location.href;
	for ( const link of nav.querySelectorAll( '.mega-menu__content a' ) ) {
		if ( link.getAttribute( 'href' ) === currentUrl ) {
			link.classList.add( 'is-current' );
		}
	}
}

function init() {
	const navs = document.querySelectorAll( '.js-navigation[data-navigation-type="desktop"]' );
	for ( const nav of navs ) {
		initDropdownMenu( nav );
	}
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
